import { Scheduler } from '../../../core/utils/Scheduler';
import { MayBe, Nullable } from '@rotomeca/utils';
import BnumElementInternal from '../../bnum-element-states';
import style from './bnum-header.less';
import { Autobind, Data, Define, Fire } from '../../../core/decorators';
import { TAG_HEADER } from '../../../core/utils/constants/tags';
import {
	CLASS_HEADER_CONTAINER,
	CLASS_HEADER_CUSTOM,
	CLASS_HEADER_LEFT,
	CLASS_HEADER_MODIFIER,
	CLASS_HEADER_RIGHT,
	CLASS_HEADER_TITLE,
	CSS_VARIABLE_BACKGROUND_IMAGE,
	DATA_BACKGROUND,
	ID_TITLE_CUSTOM,
	ID_TITLE_TEXT,
	PART_HEADER_CONTAINER,
	PART_HEADER_CUSTOM,
	PART_HEADER_LEFT,
	PART_HEADER_RIGHT,
	PART_HEADER_TITLE,
	SLOT_NAME_ACTIONS,
	SLOT_NAME_AVATAR,
	SLOT_NAME_LOGO,
	SLOT_NAME_TITLE,
	STATE_WITH_BACKGROUND,
} from './bnum-header.internal/constants';
import { Listener } from 'core/decorators/listener';
import { onBackgroundChangedInitializer } from './bnum-header.internal/listeners';
import {
	BackgroundChangedEvent,
	HTMLBnumHeaderUi,
	MenuClickedEvent,
} from './bnum-header.internal/types';
import { UI } from 'core/decorators/ui';
import { HTMLBnumButtonIcon } from 'components/atoms';
import { HTMLBnumHide } from 'components/molecules';
import { CustomDom } from 'components/bnum-element';

//#region Types
type HTMLBnumHeaderCreateOption = {
	background?: Nullable<string>;
};
//#endregion Types

//#region Template
const TEMPLATE = (
	<div class={CLASS_HEADER_MODIFIER} part={CLASS_HEADER_MODIFIER}>
		<div part={PART_HEADER_CONTAINER} class={CLASS_HEADER_CONTAINER}>
			<div part={PART_HEADER_LEFT} class={CLASS_HEADER_LEFT}>
				<HTMLBnumHide breakdown="touch" mode="up">
					<HTMLBnumButtonIcon id="menu">menu</HTMLBnumButtonIcon>
				</HTMLBnumHide>
				<slot name={SLOT_NAME_LOGO}></slot>
				<HTMLBnumHide breakdown="touch">
					<slot name={SLOT_NAME_TITLE}></slot>
					<h1
						part={PART_HEADER_TITLE}
						id={ID_TITLE_TEXT}
						class={CLASS_HEADER_TITLE}
						hidden
					></h1>
					<div
						part={PART_HEADER_CUSTOM}
						id={ID_TITLE_CUSTOM}
						class={CLASS_HEADER_CUSTOM}
						hidden
					></div>
				</HTMLBnumHide>
			</div>
			<div part={PART_HEADER_RIGHT} class={CLASS_HEADER_RIGHT}>
				<slot name={SLOT_NAME_ACTIONS}></slot>
				<slot name={SLOT_NAME_AVATAR}></slot>
			</div>
		</div>
	</div>
);
//#endregion Template

/**
 * Composant Header du Bnum
 *
 * @structure Par défaut
 * <bnum-header>
 * <img slot="logo" src="../../assets/bnumloader.svg" alt="Logo du bnum"/>
 * <div slot="title">Accueil</div>
 *
 * <bnum-secondary-button slot="actions" data-icon="add">Créer</bnum-secondary-button>
 * <bnum-icon-button slot="actions">article</bnum-icon-button>
 * <bnum-icon-button slot="actions">help</bnum-icon-button>
 * <bnum-icon-button slot="actions">settings</bnum-icon-button>
 * <bnum-icon-button slot="actions">notifications</bnum-icon-button>
 *
 * <img slot="avatar" style="border-radius: 100%" src="../../assets/avatar.png" alt="Avatar de remplacement"></img>
 * </bnum-header>
 *
 * @structure Avec image de fond
 * <bnum-header data-background="../../assets/headerbackground.gif">
 * <img slot="logo" src="../../assets/bnumloader.svg" alt="Logo du bnum"/>
 * <div slot="title">Accueil</div>
 *
 * <bnum-secondary-button slot="actions" data-icon="add">Créer</bnum-secondary-button>
 * <bnum-icon-button slot="actions">article</bnum-icon-button>
 * <bnum-icon-button slot="actions">help</bnum-icon-button>
 * <bnum-icon-button slot="actions">settings</bnum-icon-button>
 * <bnum-icon-button slot="actions">notifications</bnum-icon-button>
 *
 * <img slot="avatar" style="border-radius: 100%" src="../../assets/avatar.png" alt="Avatar de remplacement"></img>
 * </bnum-header>
 *
 * @slot logo - Slot pour le logo
 * @slot title - Slot pour le titre
 * @slot actions - Slot pour les actions
 * @slot avatar - Slot pour l'avatar
 *
 * @state with-background - Actif si une image de fond est définie
 *
 * @attr {string | undefined} (optional) data-background - Met une image de fond par défaut
 * @attr {boolean} (optional) (default: true) data-breakpoints - Si on active ou non les changements lié à la taille de l'écran.
 * @event {CustomEvent<{newBackground:Nullable<string>}>} bnum-header:background.changed - Événement déclenché lorsque l'image de fond change
 * @event {CustomEvent<{caller: HTMLBnumButtonIcon}>} bnum-header:menu - Déclancher au click du bouton "menu".
 *
 * @cssvar {block} --bnum-header-display - Définit le type d'affichage du header
 * @cssvar {60px} --bnum-header-height - Hauteur du header
 * @cssvar {#f5f6fa} --bnum-header-background-color - Couleur de fond du header
 * @cssvar {1px solid #e5e7eb} --bnum-header-border-bottom - Bordure basse du header
 * @cssvar {8px} --bnum-header-left-gap - Espace à gauche entre les éléments du header
 * @cssvar {24px} --bnum-header-right-gap - Espace à droite entre les éléments du header
 * @cssvar {45px} --bnum-header-logo-height - Hauteur du logo dans le header
 * @cssvar {none} --bnum-header-background-image - Image de fond du header (par défaut aucune)
 * @cssvar {#ffffff} --bnum-header-with-background-color - Couleur du texte sur fond personnalisé
 * @cssvar {#ffffff} --bnum-header-main-action-border-color - Couleur de la bordure du bouton principal sur fond personnalisé
 * @cssvar {#ffffff} --bnum-header-main-action-color - Couleur du texte du bouton principal sur fond personnalisé
 * @cssvar {5px 3px} --bnum-header-background-button-padding - Padding de l'action principale
 * @cssvar {0} --bnum-header-title-margin - Marge du titre
 * @cssvar {1.25rem} --bnum-header-title-font-size - Taille de la police du titre
 */
@Define({ tag: TAG_HEADER, styles: style, template: TEMPLATE })
export class HTMLBnumHeader extends BnumElementInternal {
	//#region Private fields
	// Scheduler pour éviter le layout thrashing
	/**
	 * Scheduler pour la mise à jour du titre
	 */
	#_scheduleUpdateTitle: Nullable<Scheduler<HTMLElement | string | null>> =
		null;
	/**
	 * Scheduler pour la mise à jour de l'image de fond
	 */
	#_scheduleUpdateBackground: Nullable<Scheduler<Nullable<string>>> = null;
	//#endregion Private fields

	//#region Getters/Setters
	@UI({
		slotTitle: `slot[name="${SLOT_NAME_TITLE}"]`,
		titleText: `#${ID_TITLE_TEXT}`,
		customTitleContainer: `#${ID_TITLE_CUSTOM}`,
		menuButton: '#menu',
	})
	accessor #_ui!: HTMLBnumHeaderUi;

	/**
	 * Evènement du changement d'image de fond
	 */
	@Listener(onBackgroundChangedInitializer)
	accessor onBackgroundChanged!: BackgroundChangedEvent;

	/**
	 * Evènement au click sur le menu
	 */
	@Listener() accessor onMenuClick!: MenuClickedEvent<HTMLBnumHeader>;

	/**
	 * Données de l'image de fond
	 */
	@Data(DATA_BACKGROUND)
	accessor imgBackground: Nullable<string> = null;

	/**
	 * Si on active les changements en mode mobile ou non
	 */
	@Data() accessor breakpoints: MayBe<boolean | string> = true;

	/**
	 * Scheduler pour la mise à jour de l'image de fond
	 */
	get #_backgroundScheduler(): Scheduler<Nullable<string>> {
		return (
			this.#_scheduleUpdateBackground ??
			(this.#_scheduleUpdateBackground = new Scheduler<Nullable<string>>(val =>
				this.#_updateBackground(val),
			))
		);
	}

	//#endregion Getters/Setters

	//#region Lifecycle
	constructor() {
		super();
	}

	/**
	 * @inheritdoc
	 */
	protected _p_buildDOM(container: CustomDom): void {
		if (!(this.breakpoints ?? true)) {
			for (const hide of container.querySelectorAll(HTMLBnumHide.TAG)) {
				(hide as HTMLBnumHide).disable();
			}
		}
	}

	/**
	 * @inheritdoc
	 */
	protected _p_attach(): void {
		if (this.imgBackground !== null)
			this.#_backgroundScheduler.call(this.imgBackground);

		this.#_ui.menuButton.addEventListener('click', this._handleMenuClicked);
	}
	//#endregion Lifecycle

	//#region Public methods
	/**
	 * Change le titre dynamiquement.
	 * @param content Affiche l'élément dans le conteneur dédié.
	 */
	public setPageTitle(content: HTMLElement): this;
	/**
	 * Change le titre dynamiquement.
	 * @param text Met à jour le H1.
	 */
	public setPageTitle(text: string): this;
	/**
	 * Change le titre dynamiquement.
	 * @param element Reset le titre pour afficher le slot par défaut.
	 */
	public setPageTitle(element: null): this;
	/**
	 * Change le titre dynamiquement.
	 *
	 * @param content
	 * - String : Met à jour le H1.
	 * - HTMLElement : Affiche l'élément dans le conteneur dédié.
	 * - null : Affiche le slot par défaut.
	 */
	public setPageTitle(content: HTMLElement | string | null): this {
		// Initialisation Lazy du scheduler
		(this.#_scheduleUpdateTitle ??= new Scheduler<HTMLElement | string | null>(
			val => this.#_applyTitleUpdate(val), // On utilise 'val' fourni par le Scheduler
		)).schedule(content);

		return this;
	}

	/**
	 * Met à jour l'image de fond du header.
	 * @param urlOrData Interpréte la valeur comme une URL ou une Data URL.
	 * @returns L'instance courante pour le chaînage.
	 */
	public updateBackground(urlOrData: string): this {
		this.#_requestBackgroundUpdate(urlOrData);
		return this;
	}

	/**
	 * Supprime l'image de fond du header.
	 * @returns L'instance courante pour le chaînage.
	 */
	public clearBackground(): this {
		this.#_requestBackgroundUpdate(null);
		return this;
	}
	//#endregion Public methods

	//#region Private methods
	/**
	 * Trigger `bnum-header:menu` quand le bouton du menu est cliqué
	 * @returns Les détails de l'évènement
	 */
	@Autobind
	@Fire('bnum-header:menu')
	private _handleMenuClicked() {
		if (this.onMenuClick && this.onMenuClick.haveEvents())
			this.onMenuClick.call(this, this.#_ui.menuButton);

		return { caller: this.#_ui.menuButton };
	}

	/**
	 * Exécuté par le Scheduler (au prochain frame ou microtask)
	 * @param content Contenu à appliquer
	 */
	#_applyTitleUpdate(content: Nullable<HTMLElement | string>): void {
		// Cas "Reset" -> On veut voir le Slot
		if (!content) {
			this.#_resetVisibility(true, false, false);
			return;
		}

		// Cas "String" -> On utilise le H1 natif
		if (typeof content === 'string') {
			// Optimisation: ne toucher au DOM que si le texte change vraiment
			if (this.#_ui.titleText.textContent !== content) {
				this.#_ui.titleText.textContent = content;
			}
			this.#_resetVisibility(false, true, false);
			return;
		}

		// Cas "HTMLElement" -> On injecte dans le conteneur custom
		// On vide proprement le conteneur avant d'ajouter le nouvel élément
		this.#_ui.customTitleContainer.replaceChildren(content);
		this.#_resetVisibility(false, false, true);
	}

	/**
	 * Helper pour gérer la visibilité exclusive des 3 zones (Slot, H1, Custom)
	 * Utilise l'attribut 'hidden' standard HTML5
	 * @param showSlot Affiche le slot par défaut
	 * @param showText Affiche le H1
	 * @param showCustom Affiche le conteneur custom
	 */
	#_resetVisibility(
		showSlot: boolean,
		showText: boolean,
		showCustom: boolean,
	): void {
		if (this.#_ui.slotTitle) this.#_ui.slotTitle.hidden = !showSlot;
		if (this.#_ui.titleText) this.#_ui.titleText.hidden = !showText;
		if (this.#_ui.customTitleContainer)
			this.#_ui.customTitleContainer.hidden = !showCustom;
	}

	/**
	 * Planifie la mise à jour de l'image de fond
	 * @param value Nouvelle URL de l'image de fond, ou null pour la supprimer
	 */
	#_requestBackgroundUpdate(value: Nullable<string>): void {
		this.#_backgroundScheduler.schedule(value);
	}

	/**
	 * Met à jour l'image de fond du header
	 * @param value Nouvelle URL de l'image de fond, ou null pour la supprimer
	 */
	#_updateBackground(value: Nullable<string>): void {
		if (value) {
			this.style.setProperty(CSS_VARIABLE_BACKGROUND_IMAGE, `url(${value})`);
			this._p_addState(STATE_WITH_BACKGROUND);
		} else {
			this.style.removeProperty(CSS_VARIABLE_BACKGROUND_IMAGE);
			this._p_removeState(STATE_WITH_BACKGROUND);
		}

		this.onBackgroundChanged.call(value);
	}
	//#endregion Private methods

	//#region Static methods
	/**
	 * Génère un nouvel élément HTMLBnumHeader
	 * @returns Element créé
	 */
	static Create({
		background = null,
	}: HTMLBnumHeaderCreateOption = {}): HTMLBnumHeader {
		return (document.createElement(this.TAG) as HTMLBnumHeader).condAttr(
			background !== null,
			`data-${DATA_BACKGROUND}`,
			background!,
		);
	}
	//#endregion Static methods
}
